import { toBase64 } from 'src/AppState/FilesState/FilesState';
import { CategoriesAndGamesCollection } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import {
    CategoryType,
    CollectionType,
    InsertGameType,
    GameType,
    ProviderGameType,
    KindType,
} from 'src/domains/casino/models/models';

export const casinoMiniGamesCategoryName = 'Casino Mini Games';
export const trendingCategoryName = 'Trending';

const maxGamesInTrendingCategory = 10;

// ---> CATEGORIES

export const getCategory = (specialCategoryId: number | undefined, categoryId: string): boolean => {
    return specialCategoryId !== undefined && specialCategoryId.toString() === categoryId;
};

export const getCategoryId = (categories: Array<CategoryType>, name: string): number | undefined => {
    const category = categories.find((category) => category.name === name);
    if (category !== undefined) {
        return category.id;
    }
    return undefined;
};

export const getCategoryName = (categories: Array<CategoryType>, id: string | null): string | null => {
    if (id !== null) {
        const category = categories.find((category) => category.id === parseInt(id, 10));
        if (category !== undefined) {
            return category.name;
        }
    }
    return null;
};

export const categoriesGameBelongsTo = (categories: Array<CategoryType>, existingGameId: number): Array<number> => {
    const list: Array<number> = [];
    for (const category of categories) {
        if (category.game_ids.includes(existingGameId)) {
            list.push(category.id);
        }
    }
    return list;
};

export const hasCategoryMaxGames = (categories: Array<CategoryType>, categoryId: number | undefined): boolean => {
    const category = categories.find((category) => category.id === categoryId);
    if (category !== undefined) {
        if (category.name === trendingCategoryName) {
            return category.game_ids.length >= maxGamesInTrendingCategory;
        }
    }
    return false;
};

export const getDropdownOptions = (game: GameType, collection: CategoriesAndGamesCollection): Array<CategoryType> => {
    const { trendingGamesCategoryId, maxGamesInTrending, categories } = collection;

    const availableCategories: Array<CategoryType> = [];
    for (const category of categories) {
        const categoryGame = category.game_ids.includes(game.id);

        const trendingCategoryAvailable =
            category.id === trendingGamesCategoryId && maxGamesInTrending && !categoryGame;

        if (collection.collection === 'casino') {
            if (trendingCategoryAvailable) {
                continue;
            }
        }
        availableCategories.push(category);
    }
    return availableCategories;
};

// ---> GAMES

export const providerGameTypeToInsertGameType = (game: ProviderGameType): { id?: number } & InsertGameType => {
    return {
        id: game.id,
        launch_game_id: game.launchId,
        name: game.name,
        provider: game.provider,
        studio_id: game.studio.id,
        studio_name: game.studio.name,
        demo: game.demo,
        image: game.imageUrl,
        image_vertical: '',
        image_square: '',
        published: true,
        description: '',
        jackpot_amount: '',
        max_bet: '',
        min_bet: '',
        max_win: '',
        return_to_player: '',
    };
};

export const makeInsertGame = (dataRaw: string): ({ id?: number } & InsertGameType) | null => {
    try {
        const data = JSON.parse(dataRaw);
        return providerGameTypeToInsertGameType(data);
    } catch (error) {
        console.error('Could not parse game data', error);
        return null;
    }
};

interface HasNameType {
    name: string;
}

export const sortByName = <T extends HasNameType>(items: Array<T>): Array<T> => {
    return items.sort((itemA, itemB) => (itemA.name < itemB.name ? -1 : 1));
};

export const convertKindToCollection = (kind: KindType): CollectionType => {
    switch (kind) {
        case 'Slots':
            return 'casino';
        case 'LiveCasino':
            return 'live-casino';
        case 'VirtualSports':
            return 'virtuals';
    }
};

export const convertStudioName = (studioName: string | null | undefined, providerName: string): string => {
    return studioName === providerName.toLowerCase() ? providerName : (studioName?.toUpperCase() ?? providerName);
};

/**
 * GLOB-1499 temporary fix: casino-store isn't live yet, so the studio label alias
 * fix can't be applied at the data source. Remove once GLOB-738 migration lands.
 */
const STUDIO_NAME_OVERRIDES: Record<string, string> = {
    in: 'INSPIRED',
};

export const applyStudioNameOverride = (studioName: string): string => {
    return STUDIO_NAME_OVERRIDES[studioName.toLowerCase()] ?? studioName;
};

// ---> IMAGE UPLOAD
export const urlToFile = async (url: string, fileName: string): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type });
};

export interface ImageSizeType {
    width: string;
    height: string;
    size: string;
}

export const getDimensionFromBase64Image = async (file: File): Promise<ImageSizeType | null> =>
    new Promise((resolve, reject) => {
        const img = new Image();

        img.addEventListener('load', () => {
            const imgDimension = {
                width: img.naturalWidth.toString(),
                height: img.naturalHeight.toString(),
                size: (file.size / 1024).toFixed(2).toString(),
            };
            resolve(imgDimension);
        });

        img.onerror = (error): void => reject(error);

        void toBase64(file).then((res) => {
            if (typeof res === 'string') {
                img.src = res;
            }
        });

        return null;
    });

export const blobToFile = (blobImg: Blob, fileName: string): File => {
    const b: File = new File([blobImg], fileName, { type: blobImg.type });
    return b;
};

export const getShortName = (longName: string): string => {
    const splittedName = longName.split('/');
    const newName = splittedName.pop();
    return newName === undefined ? longName : newName;
};

export type FilterCategoryType = {
    isActive?: boolean;
    isFixed?: boolean;
    isVisibleInNavigation?: boolean;
    isVisibleInAllGames?: boolean;
    hasGames?: boolean;
    containsGameId?: number;
};

export const getCategories = (categoryList: Array<CategoryType>, filters: FilterCategoryType): Array<CategoryType> => {
    let categories = categoryList;

    if (filters.isActive !== undefined) {
        categories = categories.filter((category) => category.active === filters.isActive);
    }

    if (filters.isFixed !== undefined) {
        categories = categories.filter((category) => category.fixed === filters.isFixed);
    }

    if (filters.isVisibleInNavigation !== undefined) {
        categories = categories.filter((category) => category.visible_in_navigation === filters.isVisibleInNavigation);
    }

    if (filters.isVisibleInAllGames !== undefined) {
        categories = categories.filter((category) => category.visible_in_all_games === filters.isVisibleInAllGames);
    }

    if (filters.hasGames !== undefined) {
        if (filters.hasGames === true) {
            categories = categories.filter((category) => category.game_ids.length > 0);
        } else {
            categories = categories.filter((category) => category.game_ids.length === 0);
        }
    }

    if (filters.containsGameId !== undefined) {
        const gameId = filters.containsGameId;
        categories = categories.filter((category) => category.game_ids.includes(gameId));
    }
    return categories;
};

export const slugToLabel = (slug: string): string => {
    let result = slug.replace(/-/g, ' ');
    result = result
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    return result;
};
